import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PageHeader from '../../components/PageHeader';
import MobileEntityCard, { ActionBtn } from '../../components/MobileEntityCard';
import { cabinetDictApi, cabinetPersonApi, cabinetPlatformApi, DictItem } from '../../services/api';
import { PersonD, PlatformD } from '../../types';
import { useAuth } from '../../contexts/AuthContext';

const HOSTING_OPTIONS = [
  { code: '', label: 'Авто по URL' },
  { code: 'YOUTUBE', label: 'YouTube' },
  { code: 'VK_VIDEO', label: 'VK Видео' },
  { code: 'RUTUBE', label: 'Rutube' },
  { code: 'DZEN', label: 'Дзен' },
  { code: 'TELEGRAM', label: 'Telegram' },
  { code: 'OTHER', label: 'Другое' },
];

const hostingLabel = (cd?: string | null) =>
  HOSTING_OPTIONS.find((h) => h.code === cd)?.label || cd || '—';

const PlatformsPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  const dzenEnabled = !!user?.dzenSubmissionEnabled;
  const hostingOptions = useMemo(
    () => (dzenEnabled ? HOSTING_OPTIONS : HOSTING_OPTIONS.filter((h) => h.code !== 'DZEN')),
    [dzenEnabled],
  );

  const [items, setItems] = useState<PlatformD[]>([]);
  const [persons, setPersons] = useState<PersonD[]>([]);
  const [padTypes, setPadTypes] = useState<DictItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [padType, setPadType] = useState('web');
  const [url, setUrl] = useState('');
  const [personDId, setPersonDId] = useState<number | ''>('');
  const [hostingCd, setHostingCd] = useState('');
  const [isOwner, setIsOwner] = useState(true);
  const [syncToOrd, setSyncToOrd] = useState(true);

  const syncedPersons = useMemo(
    () => persons.filter((p) => p.syncedToOrd),
    [persons],
  );

  const load = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError(null);
      const [platforms, personsRes, types] = await Promise.all([
        cabinetPlatformApi.list(),
        cabinetPersonApi.list().catch(() => [] as PersonD[]),
        cabinetDictApi.padTypes().catch(() => [] as DictItem[]),
      ]);
      setItems(platforms);
      setPersons(personsRes);
      setPadTypes(types);
      if (!padType && types.length) setPadType(types[0].code);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Ошибка загрузки');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetForm = () => {
    setName('');
    setPadType(padTypes[0]?.code || 'web');
    setUrl('');
    setPersonDId('');
    setHostingCd('');
    setIsOwner(true);
    setSyncToOrd(true);
  };

  const create = async () => {
    if (!name.trim() || !padType || !url.trim() || !personDId) {
      setError('Заполните название, тип, ссылку и контрагента');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      await cabinetPlatformApi.create({
        name: name.trim(),
        padType,
        url: url.trim(),
        personDId: Number(personDId),
        isOwner,
        hostingCd: hostingCd || undefined,
        syncToOrd,
      });
      setSuccess('Площадка создана');
      resetForm();
      await load(true);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Ошибка создания');
    } finally {
      setSaving(false);
    }
  };

  const sync = async (id: number) => {
    try {
      setError(null);
      await cabinetPlatformApi.sync(id);
      setSuccess('Отправлено в ОРД');
      await load(true);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Ошибка синхронизации');
    }
  };

  const remove = async (id: number, title: string) => {
    if (!window.confirm(`Удалить площадку «${title}»?`)) return;
    try {
      await cabinetPlatformApi.remove(id);
      setSuccess('Удалено');
      await load(true);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Ошибка удаления');
    }
  };

  const importFromOrd = async () => {
    try {
      setImporting(true);
      setError(null);
      const res = await cabinetPlatformApi.importFromOrd();
      setSuccess(`Импортировано из ОРД: ${res.imported}`);
      await load(true);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Ошибка импорта');
    } finally {
      setImporting(false);
    }
  };

  const actionsFor = (p: PlatformD) => (
    <>
      {!p.syncedToOrd && (
        <ActionBtn onClick={() => sync(p.platformDId)}>В ОРД</ActionBtn>
      )}
      <ActionBtn color="error" onClick={() => remove(p.platformDId, p.title)}>
        Удалить
      </ActionBtn>
    </>
  );

  return (
    <Box>
      <PageHeader
        title="Площадки"
        description="Как в ОРД: название, тип, URL, контрагент. Автосбор смотрит на URL канала и на уже подключённые каналы мониторинга. Бейдж «без сбора» — только если парсить нечего (нет URL и нет monitor_channel)."
        actions={
          <Button variant="outlined" disabled={importing} onClick={importFromOrd}>
            {importing ? 'Импорт…' : 'Импорт из ОРД'}
          </Button>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600 }}>
          Создание площадки
        </Typography>
        <Stack spacing={1.5}>
          <TextField
            label="Название *"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            placeholder="Введите название площадки"
          />
          <FormControl fullWidth>
            <InputLabel>Тип *</InputLabel>
            <Select
              label="Тип *"
              value={padType}
              onChange={(e) => setPadType(String(e.target.value))}
            >
              {padTypes.map((t) => (
                <MenuItem key={t.code} value={t.code}>
                  {t.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Ссылка *"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            fullWidth
            placeholder="Введите URL площадки"
            helperText="Например https://www.youtube.com/@channel или https://www.youtube.com/channel/UC… — не просто youtube.com"
          />
          <FormControl fullWidth>
            <InputLabel>Контрагент *</InputLabel>
            <Select
              label="Контрагент *"
              value={personDId === '' ? '' : String(personDId)}
              onChange={(e) => {
                const raw = String(e.target.value);
                setPersonDId(raw ? Number(raw) : '');
              }}
            >
              <MenuItem value="">
                <em>Выберите связанного с площадкой контрагента</em>
              </MenuItem>
              {syncedPersons.map((p) => (
                <MenuItem key={p.personDId} value={String(p.personDId)}>
                  {p.name}
                  {p.inn ? ` · ${p.inn}` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Хостинг (для ссылок)</InputLabel>
            <Select
              label="Хостинг (для ссылок)"
              value={hostingCd}
              onChange={(e) => setHostingCd(String(e.target.value))}
            >
              {hostingOptions.map((h) => (
                <MenuItem key={h.code || 'auto'} value={h.code}>
                  {h.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={<Checkbox checked={isOwner} onChange={(e) => setIsOwner(e.target.checked)} />}
            label="Владелец площадки (is_owner)"
          />
          <FormControlLabel
            control={<Checkbox checked={syncToOrd} onChange={(e) => setSyncToOrd(e.target.checked)} />}
            label="Сразу отправить в ОРД"
          />
          <Box>
            <Button variant="contained" disabled={saving} onClick={create}>
              {saving ? 'Сохранение…' : 'Создать'}
            </Button>
          </Box>
        </Stack>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : isMobile ? (
        <Stack spacing={1.5}>
          {items.map((p) => (
            <MobileEntityCard
              key={p.platformDId}
              title={p.title}
              subtitle={p.url || p.platformExternalId}
              chips={[
                <Chip key="type" size="small" label={p.padType || '—'} />,
                <Chip key="host" size="small" label={hostingLabel(p.hostingCd)} variant="outlined" />,
                <Chip
                  key="sync"
                  size="small"
                  color={p.syncedToOrd ? 'success' : 'default'}
                  label={p.syncedToOrd ? 'В ОРД' : 'Локально'}
                />,
                ...(p.statsCollectAvailable === false
                  ? [
                      <Chip
                        key="stats"
                        size="small"
                        color="warning"
                        label="без сбора"
                        title={p.statsCollectReason || p.statsCollectStatus || undefined}
                      />,
                    ]
                  : []),
              ]}
              meta={[
                { label: 'Контрагент', value: p.personName || '—' },
                { label: 'External ID', value: p.platformExternalId },
                ...(p.statsCollectAvailable === false && p.statsCollectReason
                  ? [{ label: 'Сбор', value: p.statsCollectReason }]
                  : []),
              ]}
              actions={actionsFor(p)}
            />
          ))}
          {!items.length && (
            <Typography color="text.secondary">Пока нет площадок — создайте или импортируйте из ОРД</Typography>
          )}
        </Stack>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Название</TableCell>
                <TableCell>Тип</TableCell>
                <TableCell>URL</TableCell>
                <TableCell>Контрагент</TableCell>
                <TableCell>Хостинг</TableCell>
                <TableCell>Сбор статистики</TableCell>
                <TableCell>ОРД</TableCell>
                <TableCell align="right">Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((p) => (
                <TableRow key={p.platformDId}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {p.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                      {p.platformExternalId}
                    </Typography>
                  </TableCell>
                  <TableCell>{p.padType || '—'}</TableCell>
                  <TableCell sx={{ maxWidth: 220, wordBreak: 'break-all' }}>{p.url || '—'}</TableCell>
                  <TableCell>{p.personName || '—'}</TableCell>
                  <TableCell>{hostingLabel(p.hostingCd)}</TableCell>
                  <TableCell sx={{ maxWidth: 220 }}>
                    {p.statsCollectAvailable === false ? (
                      <Box>
                        <Chip
                          size="small"
                          color="warning"
                          label={p.statsCollectStatus || 'недоступно для сбора статистики'}
                        />
                        {p.statsCollectReason && (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                            {p.statsCollectReason}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        автосбор
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={p.syncedToOrd ? 'success' : 'default'}
                      label={p.syncedToOrd ? 'Да' : 'Нет'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      {!p.syncedToOrd && (
                        <Button size="small" onClick={() => sync(p.platformDId)}>
                          В ОРД
                        </Button>
                      )}
                      <Button size="small" color="error" onClick={() => remove(p.platformDId, p.title)}>
                        Удалить
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {!items.length && (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Typography color="text.secondary">Пока нет площадок</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default PlatformsPage;
